import { DatePicker, Flex, Select, Typography } from "antd"
import type { Dayjs } from "dayjs"
import dayjs from "dayjs"
import React, { type Ref } from "react"
import { useTranslation } from "react-i18next"

import { dateToPeriodKey, periodKeyToDate } from "./period-keys"
import type {
  PerformanceGrain,
  PerformanceQuery,
  PerformanceRole
} from "./types"
import type { PerformanceUrlPatch } from "./use-performance-url-state"

const UNKNOWN_TYPE = "unknown"

interface Props {
  query: PerformanceQuery
  /** The period the server resolved, shown when the URL does not name one. */
  resolvedPeriod?: string
  projectTypes: { id: string; name: string }[]
  onChange: (patch: PerformanceUrlPatch) => void
  periodRef?: Ref<{ focus: () => void }>
}

const Labelled = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <Flex vertical gap={2}>
    <Typography.Text type="secondary" className="text-xs">
      {label}
    </Typography.Text>
    {children}
  </Flex>
)

export function PerformanceFilters({
  query,
  resolvedPeriod,
  projectTypes,
  onChange,
  periodRef
}: Props) {
  const { t } = useTranslation()
  const periodValue = periodKeyToDate(
    query.grain,
    query.period ?? resolvedPeriod
  )
  const pickerUnit = query.grain === "quarter" ? "month" : query.grain

  return (
    <Flex gap="middle" wrap align="flex-end">
      <Labelled label={t("teamPerformance.filters.grain")}>
        <Select<PerformanceGrain>
          aria-label={t("teamPerformance.filters.grain")}
          className="min-w-32"
          value={query.grain}
          onChange={(grain) => onChange({ grain })}
          options={[
            { value: "month", label: t("teamPerformance.grains.month") },
            { value: "quarter", label: t("teamPerformance.grains.quarter") },
            { value: "year", label: t("teamPerformance.grains.year") }
          ]}
        />
      </Labelled>
      <Labelled label={t("teamPerformance.filters.period")}>
        <DatePicker
          ref={periodRef as never}
          aria-label={t("teamPerformance.filters.period")}
          picker={query.grain}
          allowClear={false}
          value={periodValue}
          format={
            query.grain === "quarter"
              ? "YYYY-[Q]Q"
              : query.grain === "year"
                ? "YYYY"
                : "MMM YYYY"
          }
          disabledDate={(d: Dayjs) => d.isAfter(dayjs(), pickerUnit)}
          onChange={(d) =>
            d && onChange({ period: dateToPeriodKey(query.grain, d) })
          }
        />
      </Labelled>
      <Labelled label={t("teamPerformance.filters.role")}>
        <Select<PerformanceRole>
          aria-label={t("teamPerformance.filters.role")}
          className="min-w-40"
          value={query.role}
          onChange={(role) => onChange({ role })}
          options={[
            { value: "all", label: t("teamPerformance.roles.all") },
            {
              value: "project_manager",
              label: t("teamPerformance.roles.project_manager")
            },
            { value: "sales_pm", label: t("teamPerformance.roles.sales_pm") }
          ]}
        />
      </Labelled>
      <Labelled label={t("teamPerformance.filters.projectType")}>
        <Select<string>
          aria-label={t("teamPerformance.filters.projectType")}
          className="min-w-40"
          value={query.projectType ?? ""}
          onChange={(value) => onChange({ projectType: value || null })}
          options={[
            { value: "", label: t("teamPerformance.filters.allTypes") },
            ...projectTypes.map((type) => ({
              value: type.id,
              label: type.name
            })),
            {
              value: UNKNOWN_TYPE,
              label: t("teamPerformance.filters.unknownType")
            }
          ]}
        />
      </Labelled>
    </Flex>
  )
}
